import type { AccountInfo } from '@azure/msal-browser'

/**
 * Builds the identity-context line surfacing who the SPA is signed in as.
 * Shared by every transport: Foundry injects it as a first-turn
 * developer-role message; the Claude transport folds it into the `system`
 * prompt on every request (stateless backend, so no first-turn special
 * case).
 *
 * The bearer token already carries the same claims (oid, name,
 * preferred_username), but the model can't read JWTs — so we drop them
 * inline. The "do not repeat back" nudge prevents the "Hello, Karl-Johan
 * Petersson!" greeting-spam pattern models tend toward when a name is in
 * context.
 *
 * The OID is included explicitly because several Orion tools accept an
 * `ownerObjectId` filter (e.g. `list_initiatives`) — without the OID the
 * agent has to fetch-everything-and-filter-by-display-name, which burns a
 * tool call and confuses smaller models. `create_initiative` needs it
 * outright: the user table is populated lazily, so the signed-in user is
 * the only OID the agent can be sure resolves, and every idea is therefore
 * raised in their name. The OID is not sensitive (it's the same GUID
 * surfaced in audit columns and the user's own JWT).
 */
export function buildUserContextLine(account: AccountInfo): string | null {
  const claims = account.idTokenClaims as
    | { name?: string; preferred_username?: string; upn?: string; oid?: string }
    | undefined
  if (!claims) return null
  const name = typeof claims.name === 'string' && claims.name.length > 0 ? claims.name : null
  const email =
    (typeof claims.preferred_username === 'string' ? claims.preferred_username : undefined) ??
    (typeof claims.upn === 'string' ? claims.upn : undefined) ??
    null
  const oid = typeof claims.oid === 'string' && claims.oid.length > 0 ? claims.oid : null
  if (!name && !email && !oid) return null
  const who = name ? (email ? `${name} <${email}>` : name) : (email ?? '')
  const oidLine = oid
    ? ` Their Entra OID is ${oid} — pass it as ownerObjectId when filtering tool calls to "me" / "mine" / "I", and as the owner of any initiative they raise.`
    : ''
  return (
    `Acting on behalf of: ${who}.${oidLine} ` +
    'Use their name naturally when relevant; do not repeat this context back at them.'
  )
}
